import { Box, Button, IconButton, Modal, Slider, SxProps, Theme, Typography } from '@mui/material';
import { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import Cropper from 'react-easy-crop';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { StyleSheet } from 'react-native';
import { useThemeContext } from '../../hook/useThemeManager';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import Compressor from 'compressorjs';
import { en_config } from '../../config';
import getCroppedImg, { blobToDataURL } from '../ProfileHeaderUpload/helpers';
import { showToastMessage } from '../../utils/AppUtils';
import CustomLazyLoadImage, { CustomLazyLoadImageProps } from '../Image/CustomLazyLoadImage';
import MountainIcon from '../CustomSVGIcons/MountainIcon';
import { v4 as uuidv4 } from 'uuid';

export type TImageObj = {
    url: string;
    file: Blob | null;
};

type TAspectRatio = 'vertical' | 'horizontal' | 'square';
interface IProjectThumbnailUpload extends CustomLazyLoadImageProps {
    setPreview: (url: string, file: File) => void;
    aspectRatio: TAspectRatio;
    customAspectRation?: number;
    iconStyle?: SxProps<Theme>;
    innerIconStyle?: SxProps<Theme>;
    showDisplay?: boolean;
    initialValue?: string;
}

export interface IArea {
    x: number; // x/y are the coordinates of the top/left corner of the cropped area
    y: number;
    width: number; // width of the cropped area
    height: number; // height of the cropped area
}

/** Compress photos
 * Resize Photos
 * This should work to be a generic compressor and uploaer
 */
export const PhotoUploadedWithCropAndCompress = ({
    setPreview,
    aspectRatio,
    iconStyle,
    showDisplay,
    initialValue,
    customAspectRation,
    innerIconStyle,
    ...props
}: IProjectThumbnailUpload) => {
    const [src, setSrc] = useState<string>('');
    const [modalOpen, setModalOpen] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<IArea | null>(null);
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const onCropComplete = (croppedArea: any, croppedAreaPixels: IArea) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };
    const handleSliderChange = (e: Event) => {
        e.preventDefault();
        if (e && e.target && (e.target as unknown as { value: number }).value) {
            setZoom((e.target as unknown as { value: number }).value);
        }
    };
    const handleUploadClick = () => {
        const uploadInput = document.getElementById('uploadHeaderInput');
        uploadInput?.click();
    };
    const handleImgChange = (e: BaseSyntheticEvent) => {
        e.preventDefault();
        const maxSize = 2 * 1024 * 1024; // 5 MB in bytes
        const file = e.target.files[0];
        if (file.size > maxSize) {
            showToastMessage('Image size must be under 2MB', 'info');
        } else {
            setSrc(URL.createObjectURL(e.target.files[0]));
            setModalOpen(true);
        }
    };

    useEffect(() => {
        if (initialValue) {
            setSrc(initialValue);
        }
    }, [initialValue]);

    const clearInput = () => {
        const uploadInput = document.getElementById('uploadHeaderInput') as HTMLInputElement;
        uploadInput.value = '';
    };
    const showCroppedImage = async () => {
        try {
            const croppedImagee = await getCroppedImg(src, croppedAreaPixels!);
            const result = await fetch(croppedImagee!);
            const blob = await result.blob();
            const imgSize = blob.size / 1024;
            const quality =
                imgSize < 500
                    ? en_config.MEDIA_CONFIG.compressionQualityLow
                    : imgSize < 1000
                    ? en_config.MEDIA_CONFIG.compressionQualityMid
                    : en_config.MEDIA_CONFIG.compressionQualityHigh;
            new Compressor(blob, {
                quality: quality,
                success(file) {
                    blobToDataURL(file)
                        .then(x => {
                            const compressedFileFromBlob = new File([file], uuidv4(), {
                                type: file.type,
                            });
                            setPreview(x, compressedFileFromBlob);
                        })
                        .finally(() => {
                            setModalOpen(false);
                            clearInput();
                        });
                },
            });
        } catch (e) {
            console.error(e);
        }
    };

    const returnAspectRatio = useMemo(() => {
        return customAspectRation
            ? customAspectRation
            : aspectRatio === 'horizontal'
            ? 1.7
            : aspectRatio === 'square'
            ? 1
            : 0.5;
    }, [customAspectRation, aspectRatio]);

    return (
        <>
            <Modal sx={styles.modalStyle} open={modalOpen}>
                <Box
                    sx={{
                        border: `2px solid ${theme[themeStyle].darkGray60}`,
                        borderRadius: 2,
                        backgroundColor: theme[themeStyle].pure_black,
                        width: '100%',
                        maxWidth: { xs: '90vw', sm: '70vw', md: '60vw', lg: '40vw', xl: '30vw' },
                        flexDirection: 'column',
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: `2px solid ${theme[themeStyle].darkGray60}`,
                            padding: 1,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                onClick={() => {
                                    setModalOpen(false);
                                    clearInput();
                                }}
                                aria-label="cancel">
                                <ArrowBackIcon color="primary" />
                            </IconButton>
                            <Typography sx={{ ...styles.title, display: 'inline' }}> Go back </Typography>
                        </Box>
                        <Button
                            onClick={showCroppedImage}
                            style={styles.saveButton}
                            sx={{
                                textDecoration: 'none',
                                '&:hover': {
                                    cursor: 'pointer',
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].primary_white,
                                },
                                textTransform: 'none',
                                backgroundColor: theme[themeStyle].transparent,
                                transition: 'all 0.3s ease-in-out',
                            }}
                            size="small">
                            Save
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            width: '100%',
                            position: 'relative',
                            display: 'flex',
                            flexFlow: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '50vh',
                        }}>
                        <Cropper
                            image={src}
                            crop={crop}
                            zoom={zoom}
                            aspect={returnAspectRatio}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                            restrictPosition={false}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginTop: 1,
                            justifyContent: 'center',
                            borderTop: `2px solid ${theme[themeStyle].darkGray60}`,
                            padding: 1,
                        }}>
                        <RemoveCircleOutlineRoundedIcon sx={{ color: theme[themeStyle].primary_orange }} />
                        <Slider
                            min={1}
                            max={3}
                            step={0.1}
                            sx={styles.slider}
                            size="medium"
                            defaultValue={zoom}
                            value={zoom}
                            onChange={handleSliderChange}
                        />
                        <AddCircleOutlineRoundedIcon sx={{ color: theme[themeStyle].primary_orange }} />
                    </Box>
                </Box>
            </Modal>
            <Box
                onClick={handleUploadClick}
                id="uploaderContainer"
                sx={{
                    backgroundColor: 'transparent',
                    width: '100%',
                    zIndex: 1,
                    position: 'relative',
                    opacity: 0.6,
                    '&:hover': {
                        // hover without state example
                        opacity: 0.9,
                        '& .upload-icon': { opacity: 1 }, // Show icon on hover
                    },
                    cursor: 'pointer',
                    justifyContent: 'center',
                    objectFit: 'cover',
                    overflow: 'hidden',
                    height: '100%',
                    ...iconStyle,
                }}>
                <Box
                    sx={{
                        alignSelf: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: { xs: 7, sm: 0 },
                        height: '100%',
                    }}>
                    <IconButton
                        aria-label="upload profile picture"
                        sx={{
                            padding: 0, // reduces the default clickable area
                            borderRadius: 1, // makes it rectangular instead of circular (optional)
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            },
                            height: '100%',
                        }}>
                        {showDisplay && src && (
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <CustomLazyLoadImage
                                    src={src}
                                    style={{ height: '100%', width: '100%', borderRadius: 2 }}
                                    alt="contest_mobile_feature_image"
                                    {...props}
                                />
                                {/* Hidden by default, visible on hover */}
                                <AddPhotoAlternateRoundedIcon
                                    className="upload-icon"
                                    sx={{
                                        color: 'white',
                                        fontSize: 30,
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        opacity: 0, // Initially hidden
                                        transition: 'opacity 0.3s ease-in-out',
                                    }}
                                />
                            </Box>
                        )}
                        {!showDisplay && (
                            <Box
                                sx={{
                                    display: 'flex', // Enables Flexbox
                                    justifyContent: 'center', // Centers horizontally
                                    alignItems: 'center', // Centers vertically
                                    height: '100%',
                                    ...innerIconStyle,
                                }}>
                                <MountainIcon fill={theme[themeStyle].highlight} />
                            </Box>
                        )}

                        <br />
                        <input
                            type="file"
                            accept="image/*"
                            id="uploadHeaderInput"
                            multiple={false}
                            onChange={handleImgChange}
                            hidden
                        />
                    </IconButton>
                </Box>
            </Box>
        </>
    );
};

PhotoUploadedWithCropAndCompress.defaultProps = {
    aspectRatio: 'vertical',
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        slider: {
            width: '50%',
            color: theme[colorScheme].primary_orange,
            marginRight: 2,
            marginLeft: 2,
        },
        boxStyle: {
            width: '80%',
            position: 'relative',
            maxWidth: '40vw',
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: `2px solid ${theme[colorScheme].darkGray60}`,
            borderRadius: 2,
            padding: 1,
            backgroundColor: theme[colorScheme].pure_black,
            height: '50vh',
        },
        canvasStyle: {
            border: `5px dashed ${theme[colorScheme].gray_sidebar}`,
            borderRadius: 20,
            width: 200,
            height: 200,
        },
        modalStyle: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
        },
        saveButton: {
            borderRadius: 100,
            border: `1px solid ${theme[colorScheme].primary_orange}`,
        },
        cancelButton: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40px',
            width: 100,
            borderRadius: 100,
            border: `1px solid ${theme[colorScheme].tag_white}`,
        },
        title: {
            fontFamily: FONT_ENUM.REGULAR_HEADER,
            color: theme[colorScheme].primary_white,
        },
    });
